// @ts-nocheck
/**
 * Events form
 * Custom <events-form> web component:
 *  - validates fields on submit
 *  - sends a custom event + profile to Klaviyo via the public client-side
 *    endpoint (https://a.klaviyo.com/client/events with a public company_id)
 *  - optionally subscribes the profile to a configured Klaviyo list
 */

const KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events?company_id=';
const KLAVIYO_SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=';
const KLAVIYO_REVISION = '2024-10-15';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class EventsForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitBtn = null;
    this.statusEl = null;
    this.successEl = null;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitBtn = this.querySelector('[data-form-submit]');
    this.statusEl = this.querySelector('[data-form-status]');
    this.successEl = this.querySelector('[data-form-success]');

    this.#groupActionButtons();
    this.#bindFloatingLabels();
    this.#initPhoneFields();

    this.companyId = this.dataset.klaviyoCompanyId?.trim() || '';
    this.eventName = this.dataset.klaviyoEventName?.trim() || 'Special Event Form Submitted';
    this.listId = this.dataset.klaviyoListId?.trim() || '';
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.';

    this.form.addEventListener('submit', this.#handleSubmit.bind(this));

    // Only clear errors on input; do NOT validate on blur — original
    // Shopify Forms only validates on submit.
    this.form.querySelectorAll('input,textarea').forEach((field) => {
      field.addEventListener('input', () => this.#clearFieldError(field));
    });
  }

  #groupActionButtons() {
    const submit = this.form.querySelector('[data-form-submit]');
    const link = this.form.querySelector('.s-events-form__shop-link');
    if (!submit || !link) return;
    if (submit.closest('.s-events-form__actions')) return;

    // Find a common ancestor between link and submit
    let common = link.parentElement;
    while (common && !common.contains(submit)) common = common.parentElement;
    if (!common) return;

    // Walk up from link and submit until reaching a direct child of the common parent
    const ancestorIn = (el, parent) => {
      let cur = el;
      while (cur && cur.parentElement !== parent) cur = cur.parentElement;
      return cur;
    };
    const linkAnchor = ancestorIn(link, common);
    const submitAnchor = ancestorIn(submit, common);
    if (!linkAnchor || !submitAnchor) return;

    const actions = document.createElement('div');
    actions.className = 's-events-form__actions';
    common.insertBefore(actions, linkAnchor);
    // Move link and submit (preserving their original block wrappers) into the actions row
    actions.appendChild(linkAnchor);
    actions.appendChild(submitAnchor);
  }

  #initPhoneFields() {
    const phoneFields = this.form.querySelectorAll('[data-events-form-phone] input[type="tel"]');
    if (!phoneFields.length) return;

    const init = () => {
      if (!window.intlTelInput) return false;
      this.phoneItis = [];
      phoneFields.forEach((input) => {
        const wrap = input.closest('[data-events-form-phone]');
        const initialCountry = wrap?.dataset.initialCountry || 'us';
        const preferred = (wrap?.dataset.preferred || 'us,ca,gb,au')
          .split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        const iti = window.intlTelInput(input, {
          initialCountry,
          countryOrder: preferred,
          separateDialCode: false,
          formatAsYouType: true,
          nationalMode: false,
          autoPlaceholder: 'off',
          loadUtils: () => import('https://cdn.jsdelivr.net/npm/intl-tel-input@25.3.1/build/js/utils.js'),
        });
        // Prefill the dial code so the value matches the selected country
        // (the original Shopify Forms shows "+1 " by default for US).
        const seed = () => {
          if (input.value && input.value.trim().length > 0) return;
          const data = iti.getSelectedCountryData?.();
          if (data?.dialCode) input.value = `+${data.dialCode} `;
        };
        seed();
        input.addEventListener('countrychange', () => {
          input.value = '';
          seed();
        });
        // Match the original Shopify Forms format: spaces, not hyphens.
        input.addEventListener('input', () => {
          if (input.value.includes('-')) {
            const cursor = input.selectionStart;
            input.value = input.value.replace(/-/g, ' ');
            try { input.setSelectionRange(cursor, cursor); } catch {}
          }
        });
        // Block letter / punctuation keystrokes at the source — without
        // touching `input.value`, so iti's formatAsYouType keeps working.
        input.addEventListener('keydown', (e) => {
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          if (e.key.length > 1) return; // navigation / control keys
          if (/^[\d+\-\s()]$/.test(e.key)) return;
          e.preventDefault();
        });
        // Strip non-allowed characters on paste only.
        input.addEventListener('paste', (e) => {
          const text = e.clipboardData?.getData('text') || '';
          const clean = text.replace(/[^\d+\-\s()]/g, '');
          if (clean !== text) {
            e.preventDefault();
            const start = input.selectionStart ?? input.value.length;
            const end = input.selectionEnd ?? input.value.length;
            input.value = input.value.slice(0, start) + clean + input.value.slice(end);
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
        this.phoneItis.push({ input, iti });
        // Remove iti's native tooltip ("United States: +1") on the flag pill.
        const stripTitles = () => {
          const root = input.closest('.s-events-form__field-control--phone');
          root?.querySelectorAll('.iti__selected-country, .iti__selected-country *').forEach(el => el.removeAttribute('title'));
        };
        stripTitles();
        input.addEventListener('countrychange', stripTitles);
        // Apply high-res flag URL after iti renders the flag class
        const setFlagImage = () => {
          const data = iti.getSelectedCountryData?.();
          if (!data?.iso2) return;
          const flagEl = input.closest('.s-events-form__field-control--phone')?.querySelector('.iti__flag');
          if (flagEl) flagEl.style.setProperty('--events-form-flag-url', `url(https://cdn.shopify.com/static/images/flags/${data.iso2}.svg)`);
        };
        setFlagImage();
        input.addEventListener('countrychange', setFlagImage);
        // iti applies an inline padding-left to make room for its overlay
        // flag; we render the selector as a separate sibling, so reset it.
        const fixPadding = () => {
          input.style.setProperty('padding-left', '15px', 'important');
          input.style.setProperty('padding-right', '15px', 'important');
        };
        fixPadding();
        input.addEventListener('countrychange', fixPadding);
        // Refresh floating-label "is-filled" state since the input may end up
        // pre-filled when iti loads utils / dialing prefix.
        const control = input.closest('.s-events-form__field-control');
        const sync = () => {
          if (input.value && input.value.trim().length > 0) control?.classList.add('is-filled');
          else control?.classList.remove('is-filled');
        };
        input.addEventListener('input', sync);
        input.addEventListener('countrychange', sync);
        sync();
      });
    };

    if (init() === false) {
      // Wait for the iti script to load (it has defer).
      const wait = setInterval(() => {
        if (init() !== false) clearInterval(wait);
      }, 50);
      // Give up after 5s to avoid leaking the interval.
      setTimeout(() => clearInterval(wait), 5000);
    }
  }

  #bindFloatingLabels() {
    this.form.querySelectorAll('input, textarea').forEach((el) => {
      const control = el.closest('.s-events-form__field-control');
      if (!control) return;
      const update = () => {
        if (el.value && el.value.trim().length > 0) control.classList.add('is-filled');
        else control.classList.remove('is-filled');
      };
      el.addEventListener('input', update);
      el.addEventListener('change', update);
      el.addEventListener('focus', () => control.classList.add('is-focused'));
      el.addEventListener('blur', () => control.classList.remove('is-focused'));
      update();
    });
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.isSubmitting) return;

    this.#setStatus('', null);

    const fields = Array.from(this.form.querySelectorAll('input,textarea'));
    let firstInvalid = null;
    for (const field of fields) {
      if (!this.#validateField(field) && !firstInvalid) firstInvalid = field;
    }

    if (firstInvalid) {
      firstInvalid.focus();
      this.#setStatus('Please correct the highlighted fields.', 'error');
      return;
    }

    const payload = this.#collectPayload(fields);

    if (!payload.email) {
      this.#setStatus('Email is required.', 'error');
      return;
    }

    if (!this.companyId) {
      console.error('[events-form] Klaviyo company_id is not configured.');
      this.#setStatus(this.errorMessage, 'error');
      return;
    }

    this.#setSubmitting(true);

    try {
      await this.#sendToKlaviyo(payload);
      this.#showSuccess();
    } catch (err) {
      console.error('[events-form] Klaviyo submission failed:', err);
      this.#setStatus(this.errorMessage, 'error');
      this.#setSubmitting(false);
    }
  };

  #collectPayload(fields) {
    const properties = {};
    let email = '';
    let phone = '';

    for (const field of fields) {
      const key = field.dataset.klaviyoKey || field.name;
      if (!key) continue;
      const value = (field.value || '').trim();
      if (!value) continue;

      if (field.dataset.isEmail === 'true' || field.type === 'email') {
        email = value;
        properties[key] = value;
        // also expose canonical "email" for downstream Klaviyo flows
        if (key !== 'email') properties.email = value;
      } else if (field.type === 'tel') {
        const itiEntry = (this.phoneItis || []).find(e => e.input === field);
        const e164 = itiEntry?.iti?.getNumber?.();
        phone = e164 || this.#normalizePhone(value, field.dataset.countryCode || '');
        properties[key] = phone || value;
      } else {
        properties[key] = value;
      }
    }

    return { email, phone, properties };
  }

  #normalizePhone(value, code) {
    if (!value) return '';
    const digits = value.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) return digits;
    if (!code) return digits;
    return code.replace(/[^\d+]/g, '') + digits;
  }

  async #sendToKlaviyo({ email, phone, properties }) {
    const profile = { email };
    if (phone) profile.phone_number = phone;
    if (properties.fullname || properties.name) {
      const name = properties.fullname || properties.name;
      const parts = name.split(/\s+/);
      profile.first_name = parts[0];
      if (parts.length > 1) profile.last_name = parts.slice(1).join(' ');
    }

    const body = {
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name: this.eventName } } },
          profile: { data: { type: 'profile', attributes: profile } }
        }
      }
    };

    const url = KLAVIYO_EVENTS_URL + encodeURIComponent(this.companyId);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    });

    if (!response.ok && response.status !== 202) {
      const text = await response.text().catch(() => '');
      throw new Error(`Klaviyo responded ${response.status}: ${text}`);
    }

    // If a list ID is configured, also subscribe the profile to that list.
    // Klaviyo's /client/events endpoint does not perform list membership;
    // we have to hit /client/subscriptions separately.
    if (this.listId) {
      await this.#subscribeToList({ email, phone, profile });
    }
  }

  async #subscribeToList({ email, phone, profile }) {
    const subAttrs = {
      email,
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };
    if (phone) {
      subAttrs.phone_number = phone;
      subAttrs.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (profile.first_name) subAttrs.first_name = profile.first_name;
    if (profile.last_name) subAttrs.last_name = profile.last_name;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.eventName || 'Events form',
          profile: { data: { type: 'profile', attributes: subAttrs } }
        },
        relationships: {
          list: { data: { type: 'list', id: this.listId } }
        }
      }
    };

    const url = KLAVIYO_SUBSCRIPTIONS_URL + encodeURIComponent(this.companyId);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      });
      if (!response.ok && response.status !== 202) {
        const text = await response.text().catch(() => '');
        console.warn(`[events-form] Klaviyo subscription ${response.status}: ${text}`);
      }
    } catch (err) {
      // Do not fail the whole submission if list subscription fails — the
      // event POST already succeeded.
      console.warn('[events-form] Klaviyo list subscription failed:', err);
    }
  }

  #validateField(field) {
    const wrapper = field.closest('.s-events-form__field');
    const errorEl = wrapper?.querySelector('[data-field-error]');
    const value = (field.value || '').trim();
    const isEmail = field.dataset.isEmail === 'true' || field.type === 'email';
    let message = '';

    if (field.required && !value) {
      message = isEmail ? 'Email is required' : 'Field is required';
    } else if (value && isEmail && !EMAIL_REGEX.test(value)) {
      message = 'Enter a valid email';
    } else if (value && field.type === 'tel') {
      const itiEntry = (this.phoneItis || []).find(e => e.input === field);
      if (itiEntry?.iti && typeof itiEntry.iti.isValidNumber === 'function') {
        if (!itiEntry.iti.isValidNumber()) message = 'Enter a valid phone number';
      } else {
        const digits = value.replace(/\D/g, '');
        if (digits.length < 7) message = 'Enter a valid phone number';
      }
    }

    if (message) {
      wrapper?.classList.add('s-events-form__field--invalid');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      field.setAttribute('aria-invalid', 'true');
      return false;
    }

    this.#clearFieldError(field);
    return true;
  }

  #clearFieldError(field) {
    const wrapper = field.closest('.s-events-form__field');
    const errorEl = wrapper?.querySelector('[data-field-error]');
    wrapper?.classList.remove('s-events-form__field--invalid');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    field.removeAttribute('aria-invalid');
  }

  #setStatus(message, state) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      this.statusEl.removeAttribute('data-state');
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    if (state) this.statusEl.setAttribute('data-state', state);
  }

  #setSubmitting(submitting) {
    this.isSubmitting = submitting;
    if (!this.submitBtn) return;
    this.submitBtn.disabled = submitting;
    this.submitBtn.dataset.loading = submitting ? 'true' : 'false';
    const spinner = this.submitBtn.querySelector('[data-submit-spinner]');
    if (spinner) spinner.hidden = !submitting;
  }

  #showSuccess() {
    // Reset the submit button so the spinner doesn't keep running while the
    // form is being torn down (avoids an "infinite loader" if the success
    // node is missing or transition glitches).
    this.#setSubmitting(false);
    this.form.hidden = true;
    this.form.style.display = 'none';
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.style.display = '';
      this.successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    this.dispatchEvent(new CustomEvent('events-form:submitted', { bubbles: true }));
  }
}

if (!customElements.get('events-form')) {
  customElements.define('events-form', EventsForm);
}
